import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, on, createReducer } from '@ngrx/store';

import { auditLogAdapter } from '../shared-audit.adapters';
import {
  SimulatedFlightSkeleton,
  SimulatedFlightAuditLog
} from '../../+models/simulated-flight.audit.model';
import * as actions from './simulated-flight.actions';

// States
interface SimulatedFlightSkeletonsState extends EntityState<SimulatedFlightSkeleton> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  logbookSelectedPilot: number;
}

interface SimulatedFlightAuditState extends EntityState<SimulatedFlightAuditLog> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

// Adapters
const simulatedFlightSkeletonsAdapter = createEntityAdapter<SimulatedFlightSkeleton>({
  selectId: p => p.simulatedFlightId
});

// Entity selectors
export const simulatedFlightSkeletonsSelectors = simulatedFlightSkeletonsAdapter.getSelectors();

// Initial states
const initialPagedState = {
  loading: false,
  loaded: false,
  error: null,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
};

const simulatedFlightSkeletonInitialState = simulatedFlightSkeletonsAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: null,
  logbookSelectedPilot: 0
});

const simulatedFlightAuditInitialState = auditLogAdapter.getInitialState(initialPagedState);

export interface State {
  simulatedFlightSkeletons: SimulatedFlightSkeletonsState;
  simulatedFlightAudit: SimulatedFlightAuditState;
}

const initialState: State = {
  simulatedFlightSkeletons: simulatedFlightSkeletonInitialState,
  simulatedFlightAudit: simulatedFlightAuditInitialState
};

const simulatedFlightReducer = createReducer<State>(
  initialState,
  on(actions.ResetSimulatedFlight, state => {
    const logbookSelectedPilot = state.simulatedFlightSkeletons.logbookSelectedPilot;

    state = initialState;

    return {
      ...state,
      simulatedFlightSkeletons: {
        ...state.simulatedFlightSkeletons,
        logbookSelectedPilot
      }
    };
  }),

  on(actions.UpdateLogbookSelectedPilot, (state, action) => ({
    ...state,
    simulatedFlightSkeletons: {
      ...state.simulatedFlightSkeletons,
      logbookSelectedPilot: action.crewMemberId
    }
  })),

  on(actions.LoadSimulatedFlightSkeletons, state => ({
    ...state,
    simulatedFlightSkeletons: {
      ...state.simulatedFlightSkeletons,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.SimulatedFlightSkeletonsLoaded, (state, action) => ({
    ...state,
    simulatedFlightSkeletons: {
      ...state.simulatedFlightSkeletons,
      ...simulatedFlightSkeletonsAdapter.setAll(action.skeletons, state.simulatedFlightSkeletons),
      loading: false,
      loaded: true,
      error: null
    }
  })),

  on(actions.LoadSimulatedFlightAudit, (state, action) => ({
    ...state,
    simulatedFlightAudit: {
      ...state.simulatedFlightAudit,
      loading: true,
      loaded: false,
      error: null,
      lastAuditLogId:
        action.simulatedFlightId === state.simulatedFlightAudit.selectedId
          ? state.simulatedFlightAudit.lastAuditLogId
          : null
    }
  })),

  on(actions.SimulatedFlightAuditLoaded, (state, action) => ({
    ...state,
    simulatedFlightAudit: {
      ...state.simulatedFlightAudit,
      ...(!state.simulatedFlightAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.simulatedFlightAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.simulatedFlightAudit)),
      loading: false,
      loaded: true,
      error: null,
      selectedId: action.simulatedFlightId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId:
        action.audits.resultSet && action.audits.resultSet.length > 0
          ? Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
          : state.simulatedFlightAudit.lastAuditLogId
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return simulatedFlightReducer(state, action);
}
